import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { letterToEditorHtml, LetterSize, serializeLetterHtml } from '../../utils/quoteLetter';

interface LetterComposerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
}

type ListKind = '' | 'bullet' | 'number';

function normalize(value: string): string {
  return String(value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function isSizeSpan(node: Element | null): node is HTMLElement {
  return node instanceof HTMLElement && (node.classList.contains('letter-size-small') || node.classList.contains('letter-size-large'));
}

function normalizeFontTags(editor: HTMLElement) {
  editor.querySelectorAll('font').forEach((font) => {
    const parent = font.parentNode;
    if (!parent) return;
    const fontSize = Number(font.getAttribute('size') || '');
    const size: LetterSize | null = fontSize > 0 && fontSize < 3 ? 'small' : fontSize >= 5 ? 'large' : fontSize ? 'normal' : null;
    if (!size || size === 'normal') {
      while (font.firstChild) parent.insertBefore(font.firstChild, font);
      font.remove();
      return;
    }
    const span = document.createElement('span');
    span.className = `letter-size-${size}`;
    while (font.firstChild) span.appendChild(font.firstChild);
    font.replaceWith(span);
  });
}

function flattenLetterSizes(editor: HTMLElement) {
  let guard = 0;
  let nested = editor.querySelector('.letter-size-small .letter-size-small, .letter-size-small .letter-size-large, .letter-size-large .letter-size-small, .letter-size-large .letter-size-large');
  while (nested instanceof HTMLElement && nested.parentElement && guard < 20) {
    const outer = nested.parentElement;
    if (!isSizeSpan(outer)) break;
    if (outer.childNodes.length === 1) {
      outer.replaceWith(nested);
    } else {
      const parent = outer.parentNode;
      if (!parent) break;
      const before = document.createElement('span');
      const after = document.createElement('span');
      before.className = outer.className;
      after.className = outer.className;
      while (outer.firstChild && outer.firstChild !== nested) before.appendChild(outer.firstChild);
      while (nested.nextSibling) after.appendChild(nested.nextSibling);
      if (before.childNodes.length) parent.insertBefore(before, outer);
      parent.insertBefore(nested, outer);
      if (after.childNodes.length) parent.insertBefore(after, outer);
      outer.remove();
    }
    guard += 1;
    nested = editor.querySelector('.letter-size-small .letter-size-small, .letter-size-small .letter-size-large, .letter-size-large .letter-size-small, .letter-size-large .letter-size-large');
  }
}

function releaseSize(text: Text) {
  const parent = text.parentElement;
  if (!parent || !isSizeSpan(parent)) return;
  if (parent.childNodes.length === 1) {
    parent.replaceWith(text);
    return;
  }
  const holder = parent.parentNode;
  if (!holder) return;
  const before = document.createElement('span');
  const after = document.createElement('span');
  before.className = parent.className;
  after.className = parent.className;
  while (parent.firstChild && parent.firstChild !== text) before.appendChild(parent.firstChild);
  while (text.nextSibling) after.appendChild(text.nextSibling);
  if (before.childNodes.length) holder.insertBefore(before, parent);
  holder.insertBefore(text, parent);
  if (after.childNodes.length) holder.insertBefore(after, parent);
  parent.remove();
}

function textsInRange(editor: HTMLElement, range: Range): Text[] {
  if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
    const start = range.startContainer as Text;
    if (range.startOffset < start.length) {
      const next = start.splitText(range.startOffset);
      if (range.endContainer === start) range.setEnd(next, range.endOffset - range.startOffset);
    }
  }
  if (range.endContainer.nodeType === Node.TEXT_NODE) {
    const end = range.endContainer as Text;
    if (range.endOffset > 0 && range.endOffset < end.length) end.splitText(range.endOffset);
  }
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    if (current.nodeType !== Node.TEXT_NODE || !current.textContent) {
      current = walker.nextNode();
      continue;
    }
    const text = current as Text;
    const probe = document.createRange();
    probe.selectNodeContents(text);
    const overlaps = range.compareBoundaryPoints(Range.END_TO_START, probe) < 0
      && range.compareBoundaryPoints(Range.START_TO_END, probe) > 0;
    if (overlaps) nodes.push(text);
    current = walker.nextNode();
  }
  return nodes;
}

function paintSize(editor: HTMLElement, range: Range, size: LetterSize) {
  textsInRange(editor, range).forEach((text) => {
    releaseSize(text);
    if (size === 'normal') return;
    const span = document.createElement('span');
    span.className = `letter-size-${size}`;
    text.parentNode?.insertBefore(span, text);
    span.appendChild(text);
  });
  flattenLetterSizes(editor);
}

const LetterComposer: React.FC<LetterComposerProps> = ({ id, value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  const savedRange = useRef<Range | null>(null);
  const [marks, setMarks] = useState({ bold: false, italic: false, underline: false, size: 'normal' as LetterSize, list: '' as ListKind });
  valueRef.current = value;

  const publish = () => {
    const editor = editorRef.current;
    if (!editor) return;
    normalizeFontTags(editor);
    const next = serializeLetterHtml(editor);
    if (next !== normalize(valueRef.current)) onChange(next);
  };

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;
    const stored = normalize(value);
    if (serializeLetterHtml(editor) === stored) {
      if (!stored && editor.childNodes.length) editor.innerHTML = '';
      return;
    }
    editor.innerHTML = letterToEditorHtml(value);
  }, [value]);

  const rememberRange = () => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection || !selection.rangeCount || !selection.anchorNode || !editor.contains(selection.anchorNode)) return;
    savedRange.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreRange = () => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    const range = savedRange.current;
    if (!editor || !selection || !range) return;
    editor.focus();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const apply = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false);
    publish();
    readMarks();
  };

  const applySize = (size: LetterSize) => {
    const editor = editorRef.current;
    if (!editor) return;
    if (savedRange.current) restoreRange();
    else editor.focus();
    const selection = document.getSelection();
    if (selection && selection.rangeCount && selection.anchorNode && editor.contains(selection.anchorNode) && !selection.getRangeAt(0).collapsed) {
      paintSize(editor, selection.getRangeAt(0), size);
      publish();
      readMarks();
      return;
    }
    document.execCommand('fontSize', false, size === 'small' ? '2' : size === 'large' ? '5' : '3');
    normalizeFontTags(editor);
    flattenLetterSizes(editor);
    publish();
    readMarks();
  };

  const lineNodes = (): HTMLElement[] => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor) return [];
    if (!selection || !selection.rangeCount || !selection.anchorNode || !editor.contains(selection.anchorNode)) {
      const last = editor.lastElementChild;
      return last instanceof HTMLElement ? [last] : [];
    }
    const range = selection.getRangeAt(0);
    const hits: HTMLElement[] = [];
    Array.from(editor.children).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.tagName === 'UL' || node.tagName === 'OL') {
        Array.from(node.children).forEach((item) => {
          if (item instanceof HTMLElement && range.intersectsNode(item)) hits.push(item);
        });
        return;
      }
      if (range.intersectsNode(node)) hits.push(node);
    });
    if (hits.length) return hits;
    let current: Node | null = selection.anchorNode;
    if (current.nodeType === Node.TEXT_NODE) current = current.parentElement;
    while (current && current !== editor) {
      if (current instanceof HTMLElement && (current.tagName === 'LI' || current.parentElement === editor)) return [current];
      current = current.parentElement;
    }
    return [];
  };

  const unwrapItem = (item: HTMLElement): HTMLElement => {
    const list = item.parentElement;
    const div = document.createElement('div');
    while (item.firstChild) div.appendChild(item.firstChild);
    if (!div.childNodes.length) div.appendChild(document.createElement('br'));
    if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) {
      item.replaceWith(div);
      return div;
    }
    const after = document.createElement(list.tagName.toLowerCase());
    let sibling = item.nextElementSibling;
    while (sibling) {
      const next = sibling.nextElementSibling;
      after.appendChild(sibling);
      sibling = next;
    }
    item.remove();
    list.insertAdjacentElement('afterend', div);
    if (after.childNodes.length) div.insertAdjacentElement('afterend', after);
    if (!list.childNodes.length) list.remove();
    return div;
  };

  const wrapAsList = (block: HTMLElement, kind: 'ul' | 'ol') => {
    const item = document.createElement('li');
    while (block.firstChild) item.appendChild(block.firstChild);
    if (!item.childNodes.length) item.appendChild(document.createElement('br'));
    const previous = block.previousElementSibling;
    if (previous && previous.tagName === kind.toUpperCase()) {
      previous.appendChild(item);
      block.remove();
      return;
    }
    const list = document.createElement(kind);
    list.appendChild(item);
    block.replaceWith(list);
  };

  const mergeAdjacentLists = (editor: HTMLElement) => {
    let child = editor.firstElementChild;
    while (child) {
      const next = child.nextElementSibling;
      if (next && (child.tagName === 'UL' || child.tagName === 'OL') && child.tagName === next.tagName) {
        while (next.firstChild) child.appendChild(next.firstChild);
        next.remove();
        continue;
      }
      child = next;
    }
  };

  const toggleList = (kind: 'bullet' | 'number') => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const tag = kind === 'bullet' ? 'ul' : 'ol';
    const hits = lineNodes();
    if (!hits.length) {
      const div = document.createElement('div');
      div.appendChild(document.createElement('br'));
      editor.appendChild(div);
      wrapAsList(div, tag);
      publish();
      readMarks();
      return;
    }
    const already = hits.every((node) => node.tagName === 'LI' && node.parentElement?.tagName === tag.toUpperCase());
    if (already) hits.forEach(unwrapItem);
    else {
      hits.forEach((node) => {
        const block = node.tagName === 'LI' ? unwrapItem(node) : node;
        wrapAsList(block, tag);
      });
      mergeAdjacentLists(editor);
    }
    publish();
    readMarks();
  };

  const caretLineIndex = (editor: HTMLElement): number => {
    const selection = document.getSelection();
    if (!selection || !selection.rangeCount || !selection.anchorNode || !editor.contains(selection.anchorNode)) {
      const current = serializeLetterHtml(editor);
      return Math.max(0, (current ? current.split('\n').length : 1) - 1);
    }
    let node: Node | null = selection.anchorNode;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    let block: HTMLElement | null = null;
    while (node && node !== editor) {
      if (node instanceof HTMLElement && (node.tagName === 'LI' || node.parentElement === editor)) {
        block = node;
        break;
      }
      node = node.parentElement;
    }
    if (!block) return 0;
    let index = 0;
    let child = editor.firstElementChild;
    while (child && child !== block && !child.contains(block)) {
      index += child.tagName === 'UL' || child.tagName === 'OL' ? child.children.length : 1;
      child = child.nextElementSibling;
    }
    if (block.tagName === 'LI' && block.parentElement) {
      index += Array.from(block.parentElement.children).indexOf(block);
    }
    return Math.max(0, index);
  };

  const insertPageBreak = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const current = serializeLetterHtml(editor);
    const lines = current ? current.split('\n') : [];
    const lineIndex = caretLineIndex(editor);
    lines.splice(Math.min(lineIndex + 1, lines.length), 0, '---');
    const next = lines.join('\n');
    onChange(next);
    editor.innerHTML = letterToEditorHtml(next);
  };

  const readMarks = () => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    let next = { bold: false, italic: false, underline: false, size: 'normal' as LetterSize, list: '' as ListKind };
    if (editor && selection && selection.anchorNode && editor.contains(selection.anchorNode)) {
      try {
        next = {
          ...next,
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          underline: document.queryCommandState('underline'),
        };
      } catch {
        next = { ...next };
      }
      let current: Node | null = selection.anchorNode;
      if (current.nodeType === Node.TEXT_NODE) current = current.parentElement;
      while (current && current !== editor) {
        if (current instanceof HTMLElement) {
          if (next.size === 'normal' && current.classList.contains('letter-size-large')) next.size = 'large';
          if (next.size === 'normal' && current.classList.contains('letter-size-small')) next.size = 'small';
          if (!next.list && current.tagName === 'UL') next.list = 'bullet';
          if (!next.list && current.tagName === 'OL') next.list = 'number';
        }
        current = current.parentElement;
      }
    }
    setMarks((current) => (
      current.bold === next.bold
      && current.italic === next.italic
      && current.underline === next.underline
      && current.size === next.size
      && current.list === next.list
        ? current
        : next
    ));
  };

  useEffect(() => {
    const onSelection = () => readMarks();
    document.addEventListener('selectionchange', onSelection);
    return () => document.removeEventListener('selectionchange', onSelection);
  }, []);

  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    publish();
  };

  return (
    <div className="letter-composer">
      <div className="letter-composer-toolbar" role="toolbar" aria-label="Letter formatting">
        <button
          type="button"
          className={`letter-mark${marks.bold ? ' is-on' : ''}`}
          aria-pressed={marks.bold}
          title="Bold"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => apply('bold')}
        >
          B
        </button>
        <button
          type="button"
          className={`letter-mark letter-mark-italic${marks.italic ? ' is-on' : ''}`}
          aria-pressed={marks.italic}
          title="Italic"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => apply('italic')}
        >
          I
        </button>
        <button
          type="button"
          className={`letter-mark letter-mark-underline${marks.underline ? ' is-on' : ''}`}
          aria-pressed={marks.underline}
          title="Underline"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => apply('underline')}
        >
          U
        </button>
        <button
          type="button"
          className={`letter-mark letter-list${marks.list === 'bullet' ? ' is-on' : ''}`}
          aria-pressed={marks.list === 'bullet'}
          title="Bullets"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => toggleList('bullet')}
        >
          •
        </button>
        <button
          type="button"
          className={`letter-mark letter-list${marks.list === 'number' ? ' is-on' : ''}`}
          aria-pressed={marks.list === 'number'}
          title="Numbering"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => toggleList('number')}
        >
          1.
        </button>
        <label className="letter-size-label">
          <select
            className="letter-size-control"
            aria-label="Text size"
            value={marks.size}
            onMouseDown={rememberRange}
            onChange={(event) => applySize(event.target.value as LetterSize)}
          >
            <option value="small">Small</option>
            <option value="normal">Text</option>
            <option value="large">Large</option>
          </select>
        </label>
        <button
          type="button"
          className="letter-page-break"
          onMouseDown={(event) => event.preventDefault()}
          onClick={insertPageBreak}
        >
          Page break
        </button>
      </div>
      <div
        id={id}
        ref={editorRef}
        className="letter-composer-body"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Personal message"
        data-placeholder="Write a note for the client"
        spellCheck
        suppressContentEditableWarning
        onInput={publish}
        onBlur={publish}
        onPaste={onPaste}
        onKeyUp={readMarks}
        onMouseUp={readMarks}
        onFocus={readMarks}
      />
    </div>
  );
};

export default LetterComposer;
